import type { AppError } from '@aibi/shared';
import { Badge, Button, EmptyState, IconButton, SimpleTooltip, Spinner } from '@aibi/ui';
import { ChartNoAxesCombined, CircleAlert, Moon, RotateCcw, Sun } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useServices } from '../app/services';
import { RejectionToast } from '../workspace/components/shell/RejectionToast';
import { sameContent, type WorkspaceDocument } from '../workspace/model/workspace-document';
import { useUiDispatch, useUiState } from '../workspace/state/ui-state';
import { useWorkspace } from '../workspace/state/workspace-context';
import { WorkspaceProvider } from '../workspace/state/WorkspaceProvider';
import { ReportCanvas } from './components/ReportCanvas';
import { openReport, type OpenedReport } from './open-report';

type LoadState =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly error: AppError }
  | { readonly status: 'ready'; readonly report: OpenedReport };

/**
 * Reporte publicado (`#/reporte/<id>`): solo el lienzo con las visualizaciones. Quien lo abre
 * puede explorar, pero nada se guarda: el reporte publicado no cambia.
 */
export function ReportView({ reportId }: { readonly reportId: string }) {
  const { reports, modelCatalog } = useServices();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [resets, setResets] = useState(0);

  useEffect(() => {
    if (reports === undefined) return;
    let cancelled = false;
    void openReport({ reports, modelCatalog }, reportId).then((result) => {
      if (cancelled) return;
      setState(
        result.ok
          ? { status: 'ready', report: result.value }
          : { status: 'error', error: result.error },
      );
    });
    return () => {
      cancelled = true;
    };
  }, [reports, modelCatalog, reportId, attempt]);

  useEffect(() => {
    if (state.status === 'ready') document.title = state.report.document.name;
  }, [state]);

  if (reports === undefined) {
    return (
      <ReportMessage
        title="Reportes no disponibles"
        description="Abrir reportes publicados requiere el servidor de la plataforma."
      />
    );
  }
  if (state.status === 'loading') {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner label="Abriendo reporte" />
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <ReportMessage
        title={
          state.error.code === 'NOT_FOUND' ? 'Reporte no disponible' : 'No se pudo abrir el reporte'
        }
        description={state.error.message}
        onRetry={() => {
          setState({ status: 'loading' });
          setAttempt((value) => value + 1);
        }}
      />
    );
  }

  const { report } = state;
  return (
    <WorkspaceProvider
      key={resets}
      audience="viewer"
      model={report.model}
      initialDocument={report.document}
    >
      <ReportShell
        published={report.document}
        description={report.description}
        onReset={() => {
          setResets((value) => value + 1);
        }}
      />
    </WorkspaceProvider>
  );
}

function ReportShell({
  published,
  description,
  onReset,
}: {
  readonly published: WorkspaceDocument;
  readonly description: string;
  readonly onReset: () => void;
}) {
  const { document, model, lastCreatedCardId } = useWorkspace();
  const { queryResultSource } = useServices();
  const ui = useUiState();
  const uiDispatch = useUiDispatch();
  const explored = !sameContent(document, published);
  const dataMode = queryResultSource.modeFor?.(model.id) ?? null;
  const { maximizedCardId } = ui;

  useEffect(() => {
    if (lastCreatedCardId !== null) uiDispatch({ type: 'select-card', cardId: lastCreatedCardId });
  }, [lastCreatedCardId, uiDispatch]);

  useEffect(() => {
    if (maximizedCardId === null) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) {
        uiDispatch({ type: 'toggle-maximize', cardId: maximizedCardId });
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [maximizedCardId, uiDispatch]);

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col">
        <header className="flex min-h-12 shrink-0 items-center gap-2 border-b border-border bg-surface px-3 py-2">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <ChartNoAxesCombined aria-hidden className="size-4" />
          </span>
          <div className="min-w-0">
            <h1 className="truncate text-sm font-semibold" title={published.name}>
              {published.name}
            </h1>
            {description.trim() !== '' && (
              <p className="line-clamp-2 text-[11px] text-muted-foreground" title={description}>
                {description}
              </p>
            )}
          </div>
          {dataMode === 'preview' && (
            <Badge
              variant="warning"
              title="Los resultados son sinteticos y deterministas; no provienen de una fuente real."
            >
              Datos de demostracion
            </Badge>
          )}
          {dataMode === 'live' && (
            <Badge variant="success" title="Los datos se consultan en vivo al abrir el reporte.">
              Datos reales
            </Badge>
          )}

          <div className="ml-auto flex items-center gap-1">
            {explored && (
              <SimpleTooltip content="Sus cambios no se guardan ni modifican el reporte publicado">
                <Button size="sm" variant="outline" onClick={onReset}>
                  <RotateCcw /> Restablecer reporte
                </Button>
              </SimpleTooltip>
            )}
            <SimpleTooltip content={ui.colorScheme === 'dark' ? 'Tema claro' : 'Tema oscuro'}>
              <IconButton
                label={ui.colorScheme === 'dark' ? 'Activar tema claro' : 'Activar tema oscuro'}
                size="default"
                onClick={() => {
                  uiDispatch({ type: 'toggle-color-scheme' });
                }}
              >
                {ui.colorScheme === 'dark' ? <Sun /> : <Moon />}
              </IconButton>
            </SimpleTooltip>
          </div>
        </header>
        <div className="flex min-h-0 flex-1">
          <ReportCanvas />
        </div>
      </div>
      <RejectionToast />
    </>
  );
}

function ReportMessage({
  title,
  description,
  onRetry,
}: {
  readonly title: string;
  readonly description: string;
  readonly onRetry?: () => void;
}) {
  return (
    <div className="flex-1">
      <EmptyState
        tone="error"
        icon={<CircleAlert />}
        title={title}
        description={description}
        {...(onRetry === undefined
          ? {}
          : {
              action: (
                <Button size="sm" variant="outline" onClick={onRetry}>
                  Volver a intentar
                </Button>
              ),
            })}
      />
    </div>
  );
}
